import axios from 'axios';
import { 
  AuthResponse,
  RefreshResponse,
  LoginCredentials, 
  Profile,
  ProfileApiResponse,
  ClienteItem, 
  Fattura, 
  Referto,
  Prestazione,
  PrestazioniApiResponse,
  Storico,
  StoricoReferti,
  ApiResponse,
  BozzeApiResponse,
  BozzaItem,
  FatturaFormData,
  FatturaEditFormData,
  ClienteFormData,
  RefertoFormData,
  SpecialtiesResponse,
  TipoDocumento,
  DocumentiEmail,
  InvioEmailPayload
} from '../types';

class ApiService {
  private api: any;
  private baseURL: string;
  private onAuthError?: () => void;

  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || 'https://fatture.dev.madstudio.it/api/';
    console.log('🚀 [API] Inizializzazione ApiService con baseURL:', this.baseURL);
    console.log('🚀 [API] REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
    
    this.api = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    console.log('🚀 [API] Istanza axios creata con configurazione:', {
      baseURL: this.baseURL,
      headers: this.api.defaults.headers
    });

    this.setupInterceptors();
  }

  // Metodo per configurare il callback di logout
  setAuthErrorCallback(callback: () => void) {
    this.onAuthError = callback;
  }

  private setupInterceptors() {
    // Interceptor per aggiungere il token di autenticazione
    this.api.interceptors.request.use(
      (config: any) => {
        console.log('📤 [INTERCEPTOR] Richiesta in uscita:', {
          method: config.method,
          url: config.url,
          fullURL: `${config.baseURL}${config.url}`,
          headers: config.headers
        });
        
        const token = localStorage.getItem('access_token');
        
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          console.log('🔓 [INTERCEPTOR] Nessun token trovato - richiesta senza autenticazione');
        }
        return config;
      },
      (error: any) => {
        console.error('❌ [INTERCEPTOR] Errore nella richiesta:', error);
        return Promise.reject(error);
      }
    );

    // Interceptor per gestire il refresh del token
    this.api.interceptors.response.use(
      (response: any) => {
        return response;
      },
      async (error: any) => {
        console.error('❌ [INTERCEPTOR] Errore nella risposta:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          url: error.config?.url,
          message: error.message
        });
        
        const originalRequest = error.config;
        
        // Se è un errore 401 e non è un retry già tentato
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          // Se è la richiesta di login o refresh, non tentare il refresh
          if (originalRequest.url?.includes('auth/login/') || originalRequest.url?.includes('auth/refresh/')) {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            
            // Usa il callback invece di window.location.href per evitare refresh infiniti
            if (this.onAuthError) {
              this.onAuthError();
            } else {
              // Fallback solo se il callback non è configurato
              console.warn('🚨 [INTERCEPTOR] Callback onAuthError non configurato, uso window.location.href');
              window.location.href = '/login';
            }
            return Promise.reject(error);
          }
          
          // Tenta il refresh del token
          const refreshToken = localStorage.getItem('refresh_token');
          if (refreshToken) {
            try {
              const refreshResponse = await this.refreshToken(refreshToken);
              
              // Salva il nuovo access token (il refresh token rimane invariato)
              localStorage.setItem('access_token', refreshResponse.access_token);
              
              // Aggiorna l'Authorization header della richiesta originale
              originalRequest.headers.Authorization = `Bearer ${refreshResponse.access_token}`;
              
              return this.api.request(originalRequest);
              
            } catch (refreshError) {
              console.error('❌ [INTERCEPTOR] Errore durante refresh automatico:', refreshError);
              console.log('🔄 [INTERCEPTOR] Refresh fallito - redirect al login');
              localStorage.removeItem('access_token');
              localStorage.removeItem('refresh_token');
              
              // Usa il callback invece di window.location.href per evitare refresh infiniti
              if (this.onAuthError) {
                this.onAuthError();
              } else {
                // Fallback solo se il callback non è configurato
                console.warn('🚨 [INTERCEPTOR] Callback onAuthError non configurato, uso window.location.href');
                window.location.href = '/login';
              }
              return Promise.reject(refreshError);
            }
          } else {
            console.log('🔄 [INTERCEPTOR] Nessun refresh token disponibile - redirect al login');
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            
            // Usa il callback invece di window.location.href per evitare refresh infiniti
            if (this.onAuthError) {
              this.onAuthError();
            } else {
              // Fallback solo se il callback non è configurato
              console.warn('🚨 [INTERCEPTOR] Callback onAuthError non configurato, uso window.location.href');
              window.location.href = '/login';
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Autenticazione
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    
    try {
      const response = await this.api.post('auth/login/', credentials);
      return response.data;
    } catch (error) {
      console.error('❌ [API] Errore durante il login:', error);
      console.error('❌ [API] Configurazione axios:', {
        baseURL: this.baseURL,
        url: 'auth/login/',
        fullURL: `${this.baseURL}auth/login/`
      });
      throw error;
    }
  }

  async refreshToken(refreshToken: string): Promise<RefreshResponse> {
    console.log('🔄 [API] Tentativo di refresh token');
    console.log('🔄 [API] URL completo:', `${this.baseURL}auth/refresh/`);
    
    try {
      const response = await this.api.post('auth/refresh/', {
        refresh_token: refreshToken
      });
      console.log('✅ [API] Refresh token riuscito, risposta:', response.data);
      console.log('✅ [API] Nuovo access token presente:', !!response.data.access_token);
      return response.data;
    } catch (error) {
      console.error('❌ [API] Errore durante refresh token:', error);
      console.error('❌ [API] Configurazione axios:', {
        baseURL: this.baseURL,
        url: 'auth/refresh/',
        fullURL: `${this.baseURL}auth/refresh/`
      });
      throw error;
    }
  }

  async logout(): Promise<void> {
    console.log('🚪 [API] Tentativo di logout');
    try {
      // Nota: il backend Django potrebbe non avere un endpoint logout API
      // ma puliremo sempre i token locali
      console.log('✅ [API] Logout completato (solo lato client)');
    } catch (error) {
      console.error('❌ [API] Errore durante logout:', error);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      console.log('🧹 [API] Token rimossi dal localStorage');
    }
  }

  // Profilo utente
  async getProfile(): Promise<ProfileApiResponse> {
    console.log('👤 [API] Chiamata getProfile() a /profile/');
    try {
      const response = await this.api.get('/profile/');
      console.log('✅ [API] Risposta getProfile():', response.data);
      console.log('✅ [API] Struttura risposta:', {
        hasUser: !!response.data.user,
        hasProfile: !!response.data.profile,
        keys: Object.keys(response.data)
      });
      return response.data;
    } catch (error) {
      console.error('❌ [API] Errore in getProfile():', error);
      throw error;
    }
  }

  async updateProfile(profileData: Partial<Profile>): Promise<Profile> {
    const response = await this.api.put('/profile/edit/', profileData);
    return response.data;
  }

  // Clienti
  async getClienti(page: number = 1): Promise<ApiResponse<ClienteItem>> {
    const response = await this.api.get(`/clienti/?page=${page}`);
    return response.data;
  }

  async getCliente(id: number): Promise<ClienteItem> {
    const response = await this.api.get(`/clienti/${id}/`);
    return response.data;
  }

  async createCliente(clienteData: ClienteFormData): Promise<ClienteItem> {
    const response = await this.api.post('/clienti/create/', clienteData);
    return response.data;
  }

  async updateCliente(id: number, clienteData: Partial<ClienteFormData>): Promise<ClienteItem> {
    const response = await this.api.put(`/clienti/${id}/`, clienteData);
    return response.data;
  }

  async deleteCliente(id: number): Promise<void> {
    await this.api.delete(`/clienti/delete/${id}/`);
  }

  async searchCliente(query: string): Promise<any> {
    const response = await this.api.get(`/search-cliente/?query=${encodeURIComponent(query)}`);
    return response.data;
  }

  // Fatture
  async getFatture(specialistica?: number, page: number = 1, pageSize: number = 7): Promise<ApiResponse<Storico>> {
    const params = new URLSearchParams();
    if (specialistica) {
      params.append('specialistica', specialistica.toString());
    }
    params.append('page', page.toString());
    params.append('page_size', pageSize.toString());
    
    const queryString = params.toString();
    const url = queryString ? `/fatture/?${queryString}` : '/fatture/';
    const response = await this.api.get(url);
    return response.data;
  }

  async getFattura(id: number): Promise<Storico> {
    const response = await this.api.get(`/fatture/${id}/`);
    return response.data;
  }

  async createFattura(fatturaData: FatturaFormData): Promise<Fattura> {
    const response = await this.api.post('/fatture/create/', fatturaData);
    return response.data;
  }

  async updateFattura(id: number, fatturaData: Partial<FatturaEditFormData>): Promise<Fattura> {
    const response = await this.api.put(`/fatture/${id}/edit/`, fatturaData);
    return response.data;
  }

  async deleteFattura(id: number): Promise<void> {
    await this.api.delete(`/fatture/${id}/delete/`);
  }

  // Metodo renderFattura rimosso - non presente negli endpoint Django

  async downloadFatturaPDF(storicoId: number): Promise<Blob> {
    const response = await this.api.get(`/pdf/download/${storicoId}/`, {
      responseType: 'blob'
    });
    return response.data;
  }

  // Storico fatture
  async getStoricoFatture(): Promise<ApiResponse<Storico>> {
    const response = await this.api.get('/storico-fatture/');
    return response.data;
  }

  // Prestazioni
  async getPrestazioni(): Promise<PrestazioniApiResponse> {
    const response = await this.api.get('/prestazioni/');
    return response.data;
  }

  async createPrestazione(prestazioneData: { prestazione: string }): Promise<Prestazione> {
    const response = await this.api.post('/prestazioni/create/', prestazioneData);
    return response.data;
  }

  async deletePrestazione(id: number): Promise<void> {
    await this.api.delete(`/prestazioni/${id}/delete/`);
  }

  async searchPrestazione(query: string): Promise<Prestazione[]> {
    const response = await this.api.get(`/prestazioni/search/?prestazione=${query}&page=1`);
    
    // Gestisci la struttura paginata della risposta API
    if (response.data && typeof response.data === 'object' && 'results' in response.data) {
      // Struttura paginata: estrai le prestazioni da response.data.results[].prestazione
      return response.data.results?.map((item: any) => item.prestazione) || [];
    } else if (Array.isArray(response.data)) {
      // Array semplice di prestazioni
      return response.data;
    }
    
    return [];
  }

  async searchMedico(query: string): Promise<any> {
    try {
      const response = await this.api.get(`/search-medico-referente/?query=${encodeURIComponent(query)}`);
      return response.data;
    } catch (error) {
      console.error('Errore nella ricerca medici:', error);
      throw error;
    }
  }

  // Referti (usando StoricoReferti)
  async getReferti(specialistica?: number, page: number = 1, pageSize: number = 6): Promise<ApiResponse<StoricoReferti>> {
    console.log('🔍 getReferti chiamata con parametri:', { specialistica, page, pageSize });
    const params = new URLSearchParams();
    if (specialistica) {
      params.append('specialistica', specialistica.toString());
    }
    params.append('page', page.toString());
    params.append('page_size', pageSize.toString());
    
    const queryString = params.toString();
    const url = queryString ? `/storico-referti/?${queryString}` : '/storico-referti/';
    console.log('🌐 URL chiamata API:', url);
    const response = await this.api.get(url);
    console.log('📊 Risposta API getReferti:', response.data);
    return response.data;
  }

  async getReferto(id: number): Promise<StoricoReferti> {
    const response = await this.api.get(`/storico-referti/${id}/`);
    return response.data;
  }

  // Ricerca referti (restituisce tutti i risultati)
  async searchReferti(specialistica: number, search: string): Promise<StoricoReferti[]> {
    const params = new URLSearchParams();
    params.append('specialistica', specialistica.toString());
    if (search.trim()) {
      params.append('search', search.trim());
    }
    const response = await this.api.get(`/storico-referti/search/?${params.toString()}`);
    return response.data;
  }

  async createReferto(refertoData: RefertoFormData): Promise<StoricoReferti> {
    const response = await this.api.post('/referti/create/', refertoData);
    return response.data;
  }

  async updateReferto(id: number, refertoData: Partial<RefertoFormData>): Promise<StoricoReferti> {
    const response = await this.api.put(`/referti/${id}/edit/`, refertoData);
    return response.data;
  }

  async deleteReferto(id: number): Promise<void> {
    await this.api.delete(`/referti/${id}/delete/`);
  }

  // Metodo renderReferto rimosso - non presente negli endpoint Django

  async downloadRefertoPDF(storicoId: number): Promise<Blob> {
    const response = await this.api.get(`/pdf/download/${storicoId}/`, {
      responseType: 'blob'
    });
    return response.data;
  }

  // Salva PDF (disponibile per fatture e referti)
  async savePDF(pdfData: any): Promise<any> {
    const response = await this.api.post(
      '/pdf/save/',
      pdfData,
      pdfData instanceof FormData
        ? { headers: { 'Content-Type': 'multipart/form-data' } }
        : undefined
    );
    return response.data;
  }

  async generateRefertoPdf(html: string): Promise<Blob> {
    const response = await this.api.post('/pdf/genera/', { html }, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Storico referti
  async getStoricoReferti(): Promise<ApiResponse<StoricoReferti>> {
    const response = await this.api.get('/storico-referti/');
    return response.data;
  }

  // Bozze referti (usando Referto con filtro per medico)
  async getBozzeReferti(specialistica?: number, page: number = 1): Promise<BozzeApiResponse<BozzaItem>> {
    const params = new URLSearchParams();
    if (specialistica) {
      params.append('specialistica', specialistica.toString());
    }
    params.append('page', page.toString());
    const response = await this.api.get(`/bozze/?${params.toString()}`);
    return response.data;
  }

  async saveBozza(refertoData: Partial<RefertoFormData> | FormData): Promise<Referto> {
    const config = refertoData instanceof FormData ? {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    } : {};
    
    const response = await this.api.post('/bozze/create/', refertoData, config);
    return response.data;
  }

  async updateBozza(id: number, refertoData: Partial<RefertoFormData> | FormData): Promise<Referto> {
    const config = refertoData instanceof FormData ? {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    } : {};
    
    // Aggiungi l'ID della bozza al payload per l'aggiornamento
    if (refertoData instanceof FormData) {
      refertoData.append('pk', id.toString());
    } else {
      (refertoData as any).pk = id;
    }
    
    // Usa lo stesso endpoint di creazione ma con l'ID nel payload
    const response = await this.api.post('/bozze/create/', refertoData, config);
    return response.data;
  }

  async deleteBozza(id: number): Promise<void> {
    await this.api.delete(`/bozze/${id}/delete/`);
  }

  async getBozza(id: number): Promise<any> {
    const response = await this.api.get(`/bozze/${id}/`);
    return response.data;
  }

  async generateRefertoFromBozza(id: number): Promise<Referto> {
    const response = await this.api.post(`/bozze/${id}/generate/`);
    return response.data;
  }

  async duplicateReferto(storicoId: number): Promise<{ nuovo_storico_id: number }> {
    // Ottieni la specialistica dal localStorage
    const specialistica = localStorage.getItem('selectedSpecialty');
    const specialisticaValue = specialistica ? parseInt(specialistica) : undefined;
    
    const payload = {
      specialistica: specialisticaValue
    };
    
    const response = await this.api.post(`/referti/${storicoId}/duplicate/`, payload);
    return response.data;
  }

  // Settings e Logo
  async getLogos(): Promise<{ loghi: any[] }> {
    const response = await this.api.get('/logo/list/');
    return response.data;
  }

  async uploadLogo(file: File): Promise<{ logo: any }> {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await this.api.post('/logo/upload/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async deleteLogo(logoId: number): Promise<void> {
    await this.api.delete(`/logo/${logoId}/delete/`);
  }

  async selectLogo(logoId: number): Promise<void> {
    await this.api.post(`/logo/${logoId}/select/`);
  }

  // Statistiche dashboard
  async getDashboardStats(): Promise<any> {
    const response = await this.api.get('/dashboard/stats/');
    return response.data;
  }

  // API per nuovo referto
  async getCategories(): Promise<any> {
    // Ottieni la specialistica dal localStorage
    const specialistica = localStorage.getItem('selectedSpecialty');
    const params = specialistica ? `?specialistica=${specialistica}` : '';
    const response = await this.api.get(`/categories-referto/${params}`);
    return response.data;
  }

  async getOpzioniDropdown(): Promise<Record<string, Array<{ value: string; label: string }>>> {
    const response = await this.api.get('/opzioni-dropdown/');
    return response.data;
  }

  async uploadPDFMeasurements(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('pdf_file', file);
    const response = await this.api.post('/referto/measurements/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async generateReferto(refertoData: FormData): Promise<any> {
    const response = await this.api.post('/referti/genera/', refertoData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Metodi per le specialistiche
  async getSpecialties(): Promise<SpecialtiesResponse> {
    console.log('🏥 [API] Recupero specialistiche...');
    try {
      const response = await this.api.get('specialistiche/');
      console.log('✅ [API] Specialistiche recuperate:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ [API] Errore nel recupero specialistiche:', error);
      throw error;
    }
  }

  // API per ottenere il prossimo numero di fattura
  async getNextInvoiceNumber(year?: number): Promise<{
    next_invoice_number: number;
    year: number;
    message: string;
  }> {
    console.log('📄 [API] Recupero prossimo numero fattura per anno:', year || 'corrente');
    try {
      const currentYear = year || new Date().getFullYear();
      const response = await this.api.get(`/next-invoice-number/?year=${currentYear}`);
      console.log('✅ [API] Prossimo numero fattura recuperato:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ [API] Errore nel recupero prossimo numero fattura:', error);
      throw error;
    }
  }

  // Invio email con allegati (fatture e referti)
  async getDocumentiCollegati(tipo: TipoDocumento, id: number): Promise<DocumentiEmail> {
    const response = await this.api.get('/email/documenti-collegati/', { params: { tipo, id } });
    return response.data;
  }

  // Salva il PDF generato nel browser sul documento giusto
  async salvaPdfDocumento(tipo: TipoDocumento, id: number, pdf: Blob, nomeFile: string): Promise<void> {
    const formData = new FormData();
    formData.append('tipo', tipo);
    formData.append('id', id.toString());
    formData.append('pdf_file', pdf, nomeFile);
    await this.api.post('/email/salva-pdf/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async inviaEmail(payload: InvioEmailPayload): Promise<void> {
    await this.api.post('/email/send/', payload);
  }

  // API per proxy delle immagini (per risolvere problemi CORS nella generazione PDF)
  async proxyImage(imageUrl: string): Promise<Blob> {
    console.log('🖼️ [API] Proxy immagine:', imageUrl);
    try {
      const response = await this.api.get(`/proxy-image/?url=${encodeURIComponent(imageUrl)}`, {
        responseType: 'blob'
      });
      console.log('✅ [API] Immagine proxy recuperata con successo');
      return response.data;
    } catch (error: any) {
      console.error('❌ [API] Errore nel proxy immagine:', error);
      throw error;
    }
  }
}

export const apiService = new ApiService();

export default apiService; 